import React, { useState } from 'react';
import api from '../api';

// Why did that move?
//
// Deliberately not a modal. A dialog on every drag is a toll, and a toll gets
// paid by clicking the fastest button rather than the true one -- which is
// worse than no reason at all, because it looks like data.
//
// So the move has already landed. This is a strip underneath it, answered in
// one tap, and it waits for an answer rather than timing out.
//
// Two ways out, because they mean different things. The × closes this one and
// the next change will still ask. "Stop asking until tomorrow" is a decision
// about the rest of the day -- for a morning spent rearranging half the board,
// when answering the same question forty times is the wrong use of anyone.

const GROUPS = [
  ['materials',  'Materials'],
  ['equipment',  'Equipment'],
  ['outside',    'From outside'],
  ['production', 'Our side'],
  ['other',      ''],
];

const TEAL = '#14b8a6';

const T = {
  bar: {
    position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 22, zIndex: 1400,
    // Transparent teal over the board, with a solid edge so it reads as one
    // object rather than a smudge.
    background: 'rgba(20, 184, 166, 0.10)',
    backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
    border: `1px solid ${TEAL}`, borderRadius: 12,
    boxShadow: '0 10px 34px rgba(0,0,0,.45)',
    padding: '11px 15px 12px', display: 'flex', flexDirection: 'column', gap: 9,
    maxWidth: 'min(94vw, 800px)',
  },
  head: { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' },
  ask:  { fontSize: 13, fontWeight: 700, color: '#e5e7eb', whiteSpace: 'nowrap' },
  what: { fontSize: 12.5, color: '#cbd5d1', flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  rows: { display: 'flex', flexDirection: 'column', gap: 6 },
  row:  { display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' },

  // Louder than the chips, not quieter. A group label that fades away leaves
  // twelve chips in a heap, which is what grouping was for.
  glab: { fontFamily: 'ui-monospace, monospace', fontSize: 9.5, letterSpacing: '.13em',
          textTransform: 'uppercase', color: TEAL, fontWeight: 700,
          width: 78, flex: 'none', opacity: 0.95 },

  chip: { background: 'rgba(15, 22, 26, 0.55)', border: '1px solid rgba(20,184,166,0.35)',
          color: '#d7dee2', borderRadius: 16, padding: '3px 10px', fontSize: 11.5,
          cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', lineHeight: 1.5 },

  close: { background: 'none', border: 'none', color: '#9ca3af', fontSize: 16,
           lineHeight: 1, cursor: 'pointer', padding: '0 4px', fontFamily: 'inherit' },

  pause: { background: 'none', border: 'none', color: '#8b9398', fontSize: 11.5,
           cursor: 'pointer', fontFamily: 'inherit', padding: '3px 0',
           textDecoration: 'underline', textUnderlineOffset: 3, whiteSpace: 'nowrap' },

  foot: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 12, marginTop: -2, flexWrap: 'wrap' },

  // Which move this is. Its own time and sentence, because "Why?" over a
  // queue of three is only answerable if you know which of the three.
  when:  { fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: TEAL,
           fontWeight: 600, whiteSpace: 'nowrap' },
  count: { fontFamily: 'ui-monospace, monospace', fontSize: 10.5, fontWeight: 700,
           color: '#12161b', background: TEAL, borderRadius: 10, padding: '1px 8px',
           whiteSpace: 'nowrap' },


  // The explanation sits where the action is, and only while the pointer is on
  // it. Telling someone what a control does after they have pressed it is not
  // telling them.
  hint: { position: 'absolute', right: 12, bottom: -6, transform: 'translateY(100%)',
          background: '#12161b', border: `1px solid ${TEAL}`, borderRadius: 8,
          padding: '7px 11px', fontSize: 11.5, color: '#cbd5d1', lineHeight: 1.5,
          maxWidth: 320, boxShadow: '0 6px 18px rgba(0,0,0,.5)', zIndex: 2 },
};

// The queue, oldest first. The question on screen never changes under the
// director's hands when another move lands -- only the count grows -- because a
// question that jumps while you are reading it is a question answered wrongly.
export default function ReasonStrip({ queue = [], reasons, mode, onDone, onPause }) {
  const [busy, setBusy]   = useState(false);
  const [hint, setHint]   = useState(false);
  const [applyAll, setApplyAll] = useState(false);
  const event = queue[0] || null;
  const waiting = queue.length;

  // No timer. It used to dismiss itself after twelve seconds, which meant a
  // question could vanish while the director was still looking at the board
  // deciding what the answer was -- and a reason lost that way is lost for
  // good. It stays until it is answered, closed, or paused.

  if (!event || !reasons?.length || mode === 'off') return null;

  // One reason, or the same reason for every move still waiting. A busy
  // morning is often one cause behind several moves -- material late, and four
  // runs shuffled to cover it -- and answering that four times is the toll this
  // strip exists to avoid.
  async function pick(code) {
    setBusy(true);
    const targets = applyAll ? queue : [event];
    const done = [];
    for (const ev of targets) {
      try {
        await api.put(`/api/v1/reasons/event/${ev.event_id}`, { reason_code: code });
        done.push(ev.event_id);
      } catch { /* the move is saved; failing here costs only this reason */ }
    }
    setBusy(false);
    setApplyAll(false);
    onDone(code, done);
  }

  const time = event.at
    ? new Date(event.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  const byGroup = GROUPS
    .map(([key, label]) => [label, reasons.filter(r => r.reason_group === key)])
    .filter(([, rs]) => rs.length);

  return (
    <div style={{ ...T.bar, position: 'fixed' }} role="dialog" aria-label="Why did that move?">
      <div style={T.head}>
        <span style={T.ask}>Why?</span>
        {waiting > 1 && <span style={T.count} title="Moves still waiting for a reason, oldest first">1 of {waiting}</span>}
        {time && <span style={T.when}>{time}</span>}
        <span style={T.what} title={event.summary}>{event.summary}</span>
        {mode !== 'required' && (
          <button style={T.close} onClick={() => onDone(null, [event.event_id])} disabled={busy}
            aria-label="Skip this one" title={waiting > 1 ? 'Skip this one and go to the next' : 'Close. The next change will still ask.'}>
            &times;
          </button>
        )}
      </div>

      <div style={T.rows}>
        {byGroup.map(([label, rs]) => (
          <div key={label || 'other'} style={T.row}>
            <span style={T.glab}>{label}</span>
            {rs.map(r => (
              <button key={r.code} style={T.chip} disabled={busy}
                onClick={() => pick(r.code)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = TEAL;
                                     e.currentTarget.style.color = TEAL;
                                     e.currentTarget.style.background = 'rgba(20,184,166,0.16)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(20,184,166,0.35)';
                                     e.currentTarget.style.color = '#d7dee2';
                                     e.currentTarget.style.background = 'rgba(15,22,26,0.55)'; }}>
                {r.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {(mode !== 'required' || waiting > 1) && (
        <div style={{ ...T.foot, position: 'relative' }}>
          {waiting > 1 ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                            fontSize: 11.5, color: applyAll ? TEAL : '#9ca3af' }}>
              <input type="checkbox" checked={applyAll} onChange={e => setApplyAll(e.target.checked)}
                style={{ accentColor: TEAL, margin: 0 }} />
              Same reason for all {waiting}
            </label>
          ) : <span />}
          {mode !== 'required' && <button style={T.pause} disabled={busy}
            onMouseEnter={() => setHint(true)} onMouseLeave={() => setHint(false)}
            onFocus={() => setHint(true)} onBlur={() => setHint(false)}
            onClick={() => { setHint(false); onPause(); }}>
            Stop asking until tomorrow
          </button>}
          {hint && (
            <div style={T.hint}>
              Nothing more will be asked today, <b>on any device you sign in to</b>, and
              only for you. Changes are still recorded — only the question stops. It
              lifts by itself overnight, or turn it back on from the scheduler menu
              under <b>Asking why</b>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
